'use strict';

import { isString, deburr, debounce } from 'lodash';
import axios from 'axios';
import removeEntities from 'utils/remove-entities';

const SELECTOR = '.swiftype-autosuggest';

(function () {
    const elements = document.querySelectorAll(SELECTOR);

    if (elements.length === 0) {
        return false;
    }

    const cache = {}

    elements.forEach(element => {
        const options = {
            inputDebounce: element.dataset.searchInputDebounce || 250,
            minLength: element.dataset.searchMinLength || 2,
            maxSuggestions: element.dataset.searchMaxSuggestions || 10,
            engineKey: element.dataset.searchEngineKey,
            fields: (element.dataset.searchFields || 'title,sections').split(','),
        }

        const selectors = {
            input: element.dataset.searchInput || '.swiftype-autosuggest__input',
            list: element.dataset.searchList || '.swiftype-autosuggest__list',
            item: element.dataset.searchItem || '.swiftype-autosuggest__item',
            itemActive: element.dataset.searchItemActive || '.swiftype-autosuggest__item--active',
            link: element.dataset.searchLink || '.swiftype-autosuggest__link',
        }

        const classes = {
            containerActive: element.dataset.searchContainerActive || 'swiftype-autosuggest--active',
            item: element.dataset.searchItem || 'swiftype-autosuggest__item',
            itemActive: element.dataset.searchItemActive || 'swiftype-autosuggest__item--active',
            link: element.dataset.searchLink || 'swiftype-autosuggest__link',
        }

        const input = element.querySelector(selectors.input);
        const list = element.querySelector(selectors.list);

        const urlPrefix = element.action;
        const queryParam = input.name;

        const regex = new RegExp(`[&?]${queryParam}=([^&]+)`);
        const match = window.location.search.match(regex);
        if (match) {
            input.value = decodeURIComponent(match[1]);
        }

        let currentQuery = null;
        let totalItems = null;
        let currentItem = null;

        const resetSuggestions = () => {
            currentQuery = null;
            totalItems = null;
            currentItem = null;

            hideSuggestions();
        }

        const processQuery = debounce(query => {
            return getSuggestions(query).then(suggestions => {
                currentQuery = query;
                totalItems = suggestions.length;
                currentItem = null;

                return processSuggestions(suggestions);
            });
        }, options.inputDebounce);

        const hideSuggestions = () => {
            // Hide the container
            element.classList.remove(classes.containerActive);
        }

        const showSuggestions = () => {
            // Show the results
            element.classList.add(classes.containerActive);
        }

        const processSuggestions = suggestions => {
            // Empty the container
            while (list.firstChild) {
                list.removeChild(list.firstChild);
            }

            for (const [index, suggestion] of suggestions.entries()) {
                list.appendChild(
                    createListItem(suggestion, index + 1)
                );
            }

            if (totalItems) {
                showSuggestions();
            } else {
                hideSuggestions();
            }
        }

        const getSuggestions = query => {
            query = query.toLowerCase();

            if (query in cache) {
                return cache[query];
            }

            const params = new URLSearchParams({
                q: query,
                engine_key: options.engineKey,
                spelling: 'always',
                per_page: 5,
            });

            const url = 'https://api.swiftype.com/api/v1/public/engines/search.json?' + params.toString();

            cache[query] = axios.get(url).then(response => {
                let suggestions = [];

                if ('spelling_suggestion' in response.data.info.page) {
                    suggestions.push(
                        response.data.info.page.spelling_suggestion.text.trim()
                    );
                }

                suggestions = response.data.records.page.reduce((carry, record) => {
                    for (let k in record.highlight) {
                        if (options.fields.length && options.fields.indexOf(k) === -1) {
                            continue;
                        }

                        let suggestion = record.highlight[k];

                        // Lower case
                        suggestion = suggestion.toLowerCase();

                        // Turn into friendlier encoding
                        suggestion = deburr(suggestion);

                        // Hack to fix when you have multiple consecutive keywords
                        suggestion = suggestion.replace('</em> <em>', ' ');

                        // Split by things
                        suggestion = suggestion.split('<em>');

                        // Remove first because trash
                        suggestion.shift();

                        suggestion = suggestion.map(item => {
                            // Remove the remaining closing em
                            item = item.replace('</em>', '');

                            // Remove entities
                            item = removeEntities(item);

                            // Remove other trash
                            item = item.replace(/[^A-Za-z0-9 ]/g, ' ');

                            // Skip if suggestion is too big
                            if (item.split(' ').length > 8) {
                                return false;
                            }

                            return item;
                        });

                        // Remove nulls
                        suggestion = suggestion.filter(item => {
                            return item;
                        });

                        // Do stuff
                        for (let i = 0; i < suggestion.length; i++) {
                            let item = suggestion[i];

                            // Remove double spaces
                            item = item.replace(/\s{2,}/, ' ');

                            // Remove whitespace
                            item = item.trim();

                            // Skip if dupe
                            if (carry.indexOf(item) !== -1) {
                                continue;
                            }

                            // Add to running total
                            carry.push(item);
                        }
                    }

                    return carry;
                }, suggestions);

                // Sort by size
                suggestions.sort((a, b) => {
                    return a.length - b.length;
                });

                // Limit results
                suggestions = suggestions.splice(0, options.maxSuggestions);

                return suggestions;
            });

            return cache[query];
        }

        const createListItem = (suggestion, index) => {
            const item = document.createElement('li');
            const link = createLink(suggestion);

            item.setAttribute('class', classes.item);
            item.setAttribute('data-suggestion', suggestion);
            item.setAttribute('data-index', index);
            item.appendChild(link);

            item.addEventListener('mouseover', () => {
                currentItem = index;

                updateSelection(false);
            }, false);

            item.addEventListener('mouseout', () => {
                currentItem = null;

                updateSelection(false);
            }, false);

            return item;
        }

        const createLink = suggestion => {
            const link = document.createElement('a');
            link.setAttribute('href', `${urlPrefix}?${queryParam}=${suggestion.replace(' ', '+')}`);
            link.setAttribute('class', classes.link);
            link.innerHTML = suggestion.replace(new RegExp(currentQuery, 'ig'), '<b>$&</b>');
            return link;
        }

        const updateSelection = changeValue => {
            if (changeValue === undefined) {
                changeValue = true;
            }

            unselectItems();

            if (currentItem !== null) {
                const item = element.querySelector(selectors.item + ':nth-child(' + currentItem + ')');

                item.classList.add(classes.itemActive);

                if (changeValue === true) {
                    input.value = item.dataset.suggestion;
                }
            } else if (changeValue === true) {
                input.value = currentQuery;
            }
        }

        const unselectItems = () => {
            element.querySelectorAll(selectors.itemActive).removeClass(classes.itemActive);
        }

        const selectNext = () => {
            if (currentItem === null) {
                currentItem = 1;
            } else {
                currentItem++;

                if (currentItem > totalItems) {
                    currentItem = null;
                }
            }

            updateSelection();
        }

        const selectPrevious = () => {
            if (currentItem === null) {
                currentItem = totalItems;
            } else {
                currentItem--;

                if (currentItem === 0) {
                    currentItem = null;
                }
            }

            updateSelection();
        }

        input.addEventListener('input', event => {
            const query = event.currentTarget.value;

            if (isString(query) === false || query.length < options.minLength) {
                return resetSuggestions();
            }

            processQuery(query);
        }, false);

        input.addEventListener('keyup', event => {
            if (event.defaultPrevented) {
                return;
            }

            switch (event.key || event.code) {
                case 'ArrowDown':
                case 'Down':
                    selectNext();
                    break;
                case 'ArrowUp':
                case 'Up':
                        selectPrevious();
                    break;
                default:
                    return;
            }
        }, false);

        input.addEventListener('focus', event => {
            if (currentQuery === null) {
                return false;
            }

            if (event.currentTarget.value !== currentQuery) {
                return false;
            }

            if (totalItems) {
                showSuggestions();
            }
        }, false);

        input.addEventListener('blur', () => {
            hideSuggestions();
        }, false);
    });
}());
